import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../DatePickerStyles.css';
import './MobileJobActions.css';
import { lockScheduleScroll } from './lockScheduleScroll';
import { useHolidays } from '../../../hooks/useHolidays';
import { calendarDateKey } from './mobileCalendarCounts';

export default function MobileScheduleMoveDialog({ title = 'Move job', jobLabel, onClose, onSelect, allowUnscheduled = true, submitLabel = 'Continue', drivers = [], onAssignDriver, companyState, loadCounts }) {
    const holidayMap = useHolidays(companyState);
    const [month, setMonth] = useState(() => new Date());
    const [counts, setCounts] = useState(null);
    const [countError, setCountError] = useState(false);
    const loaderRef = useRef(loadCounts);
    loaderRef.current = loadCounts;
    useEffect(() => {
        if (!loaderRef.current) return;
        let active = true;
        setCounts(null); setCountError(false);
        Promise.resolve().then(() => loaderRef.current(month)).then(value => {
            if (active) setCounts(value);
        }).catch(() => { if (active) setCountError(true); });
        return () => { active = false; };
    }, [month]);
    const [date, setDate] = useState(null);
    const [error, setError] = useState('');
    const [driverId, setDriverId] = useState('');
    const [savingDriver, setSavingDriver] = useState(false);
    const titleId = useId();
    const dateId = useId();
    const dialogRef = useRef(null);
    useEffect(() => {
        const previousFocus = document.activeElement;
        const unlock = lockScheduleScroll();
        dialogRef.current?.focus();
        return () => {
            unlock();
            previousFocus?.focus?.();
        };
    }, []);
    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !savingDriver) { event.stopPropagation(); onClose(); }
        if (event.key !== 'Tab') return;
        const items = [...dialogRef.current.querySelectorAll('button:not(:disabled), input, select')];
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
            event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first?.focus();
        }
    };
    return createPortal(
        <div className="fixed inset-0 z-[10030] bg-black/50 flex items-center justify-center p-4" onClick={e => { if (!savingDriver && e.target === e.currentTarget) onClose(); }}>
            <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={handleKeyDown} className="bg-white rounded-2xl w-full max-w-md p-4 max-h-[90dvh] overflow-y-auto shadow-xl">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
                    <button type="button" onClick={onClose} disabled={savingDriver} className="min-h-[44px] px-3 rounded-lg border text-sm font-semibold">Close</button>
                </div>
                <p className="text-sm text-gray-600 break-words mb-4">{jobLabel}</p>
                <form onSubmit={event => {
                    event.preventDefault();
                    if (!date) return;
                    const year = date.getFullYear();
                    const month = date.getMonth();
                    const day = date.getDate();
                    // Match the existing schedule handlers' UTC calendar-day convention.
                    const selected = new Date(Date.UTC(year, month, day, 10));
                    if (selected.getUTCDay() === 0) { setError('Please select Monday through Saturday.'); return; }
                    onSelect(selected);
                }}>
                    <p id={dateId} className="text-sm font-semibold mb-2">New date</p>
                    <div role="group" aria-labelledby={dateId} className="mobile-move-calendar flex justify-center">
                        <DatePicker selected={date} onChange={value => { setDate(value); setError(''); }}
                            inline showMonthDropdown showYearDropdown dropdownMode="select"
                            onMonthChange={setMonth} onYearChange={setMonth}
                            dayClassName={value => {
                                const key = calendarDateKey(value);
                                const count = counts?.[key];
                                const workload = count == null ? '' : count === 0 ? 'mobile-calendar-empty' : count <= 3 ? 'mobile-calendar-light' : count <= 6 ? 'mobile-calendar-medium' : 'mobile-calendar-heavy';
                                return [workload, holidayMap.has(key) ? 'mobile-calendar-holiday' : ''].filter(Boolean).join(' ');
                            }}
                            calendarStartDay={1} dateFormat="dd/MM/yyyy"
                            filterDate={value => value.getDay() !== 0}
                            className="date-picker-custom" />
                    </div>
                    {loadCounts && <p className="mt-2 text-xs text-gray-500" role="status">{countError ? 'Job counts could not be loaded.' : !counts ? 'Loading job counts…' : 'Workload follows this schedule’s filters. Outlined dates are public holidays.'}</p>}
                    {counts && <div className="mobile-calendar-legend" aria-label="Workload legend">
                        <span><i className="mobile-calendar-empty" />0 jobs</span><span><i className="mobile-calendar-light" />1–3</span>
                        <span><i className="mobile-calendar-medium" />4–6</span><span><i className="mobile-calendar-heavy" />7+</span>
                    </div>}
                    {date && <p className="mt-2 text-sm text-amber-700">{(holidayMap.get(calendarDateKey(date)) || []).join(' · ')}</p>}
                    {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
                    <button type="submit" disabled={!date || savingDriver} className="mt-4 w-full min-h-[44px] rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">{submitLabel}</button>
                </form>
                {onAssignDriver && <form className="mt-4 border-t pt-4" onSubmit={async event => {
                    event.preventDefault();
                    if (!driverId || savingDriver) return;
                    setSavingDriver(true); setError('');
                    try { await onAssignDriver(driverId); }
                    catch { setError('Could not assign the driver. Please try again.'); }
                    finally { setSavingDriver(false); }
                }}>
                    <label className="block text-sm font-semibold">Driver
                        <select value={driverId} onChange={e => setDriverId(e.target.value)} className="mt-2 w-full min-h-[44px] border rounded-lg px-3 text-base">
                            <option value="">Select driver</option>
                            {drivers.map(driver => <option key={driver.id} value={driver.id}>{driver.full_name || driver.username}</option>)}
                        </select>
                    </label>
                    <button disabled={!driverId || savingDriver} type="submit" className="mt-2 w-full min-h-[44px] border rounded-lg text-sm font-semibold disabled:opacity-50">{savingDriver ? 'Assigning…' : 'Assign driver'}</button>
                </form>}
                {allowUnscheduled && <button type="button" disabled={savingDriver} onClick={() => onSelect(null)} className="mt-2 w-full min-h-[44px] rounded-lg border text-sm font-semibold">Move to unscheduled</button>}
            </div>
        </div>, document.body
    );
}
